
import React, { useState, useEffect } from 'react';
import { Copy, CheckCircle, AlertCircle, ArrowRight, RotateCw, Search, FileSearch, Database, ExternalLink, Zap } from 'lucide-react';
import PopupCard from '../PopupCard';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { docco } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { getFirestore, doc, updateDoc, getDoc } from 'firebase/firestore';

const LogsModal = ({ 
  isOpen, 
  onClose, 
  result, 
  currentUser,
  copyToClipboard
}) => {
  const [documentData, setDocumentData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch the complete document data from Firestore
  useEffect(() => {
    const fetchDocument = async () => {
      if (!isOpen || !result?.documentId) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const db = getFirestore();
        const docRef = doc(db, 'LLM Queries', result.documentId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setDocumentData({ id: docSnap.id, ...docSnap.data() });
          console.log("Document data:", docSnap.data());
        } else {
          setError("Document not found");
        }
      } catch (err) {
        console.error("Error fetching document:", err);
        setError(`Error fetching document: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDocument();
  }, [isOpen, result?.documentId]);

  // Handle copying Tango ID and updating Firestore
  const handleTangoIdCopy = () => {
    copyToClipboard(`tango_${result.documentId}`, () => {
      const element = document.getElementById(`tango-id-${result.documentId}`);
      if (element) {
        element.textContent = "Copied!";
        setTimeout(() => {
          element.textContent = `tango_${result.documentId}`;
        }, 2000);
      }
    });
    
    const db = getFirestore();
    const docRef = doc(db, 'LLM Queries', result.documentId);
    
    updateDoc(docRef, {
      sqlShared: true,
      sharedAt: new Date(),
      sharedBy: currentUser?.email || 'unknown'
    }).catch(error => {
      console.error('Error sharing query:', error);
    });
  };

  // Format time for display
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      // Create date from timestamp
      let date;
      
      // Check for Firestore timestamp format
      if (timestamp && typeof timestamp === 'object' && timestamp.seconds !== undefined) {
        date = new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else {
        date = new Date(timestamp);
      }
      
      if (isNaN(date.getTime())) {
        console.log('Invalid timestamp format:', timestamp);
        return '';
      }
      
      return date.toLocaleTimeString();
    } catch (e) {
      console.log('Error formatting time:', e);
      return '';
    }
  };

  // We'll use both the original result and the fetched document data
  const mergedData = { ...result, ...documentData };

  // Process data for the pipeline steps
  const pipelineSteps = [
    {
      name: 'Intent Processing',
      icon: Search,
      iconColor: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      active: mergedData.intentLog?.enabled,
      detail: mergedData.intentLog?.enabled 
        ? `Selected ${mergedData.intentLog.selectedTableCount || 0}/${mergedData.intentLog.allTableCount || 0} tables in ${mergedData.intentLog.processingTime ? (mergedData.intentLog.processingTime / 1000).toFixed(1) : 'N/A'}s`
        : 'Not enabled',
        extraContent: (mergedData.intentLog?.selectedTables || mergedData.intentSelected) && (
          <div className="ml-11 -mt-1">
            {/* If we have detailed table/column info, show it */}
            {mergedData.intentLog?.selectedTables ? (
            <div className="space-y-2">
              {mergedData.intentLog.selectedTables.map((tableInfo, idx) => {
                // Handle both string format (old) and object format (new)
                const tableId = typeof tableInfo === 'string' 
                  ? tableInfo 
                  : (tableInfo?.tableId || tableInfo?.table || '');
                
                const tableName = tableId ? tableId.split('.').pop() : 'Unknown Table';
                const columns = typeof tableInfo === 'object' ? tableInfo.columns : null;
                
                return (
                  <div key={idx} className="bg-white border border-indigo-200 rounded-lg p-2">
                    <div className="font-medium text-indigo-700 text-xs mb-1">
                      {tableName}
                    </div>
                    {columns && columns.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {columns.map((column, colIdx) => (
                          <span key={colIdx} className="inline-block bg-indigo-50 text-indigo-600 rounded px-1.5 py-0.5 text-xs">
                            {column}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            ) : (
              /* Fallback to old display if we only have table IDs */
              <div className="flex flex-wrap">
                {mergedData.intentSelected.map((table, idx) => (
                  <span key={idx} className="inline-block bg-indigo-50 text-indigo-700 rounded px-2 py-0.5 text-xs mr-1.5 mb-1.5">
                    {table.split('.').pop()}
                  </span>
                ))}
              </div>
            )}
          </div>
        )
    },
    // Add Action Execution step
    {
      name: 'Action Execution',
      icon: Zap,
      iconColor: (Array.isArray(mergedData.actionLog?.executed) && mergedData.actionLog.executed.length > 0)
        ? (mergedData.actionLog.executed.some(action => action.status === 'success') ? 'text-green-600' : 'text-red-600')
        : mergedData.detectedActions?.length > 0 ? 'text-blue-600' : 'text-gray-500',
      bgColor: 'bg-purple-50',
      active: mergedData.actionLog?.enabled && (
        (mergedData.intentLog?.detectedActions?.length > 0) || 
        (mergedData.detectedActions?.length > 0) || 
        (Array.isArray(mergedData.actionLog?.executed) && mergedData.actionLog.executed.length > 0)
      ),      
      status: !mergedData.actionLog?.enabled ? 'inactive' :
        mergedData.detectedActions?.length === 0 ? 'skipped' :
        (!Array.isArray(mergedData.actionLog?.executed) || mergedData.actionLog.executed.length === 0) ? 'pending' :
        mergedData.actionLog.executed.every(action => action.status === 'success') ? 'success' :
        mergedData.actionLog.executed.some(action => action.status === 'error') ? 'error' : 'warning',
        detail: !mergedData.actionLog?.enabled ? 'Not enabled' :
        ((mergedData.intentLog?.detectedActions?.length || 0) + (mergedData.detectedActions?.length || 0)) === 0 ? 'No actions detected' :
        (!Array.isArray(mergedData.actionLog?.executed) || mergedData.actionLog?.executed?.length === 0) ? 
          `${(mergedData.intentLog?.detectedActions?.length || 0) + (mergedData.detectedActions?.length || 0)} detected, pending execution` :
        `${mergedData.actionLog?.executed?.length} executed`,
        extraContent: ((mergedData.intentLog?.detectedActions?.length > 0) || (mergedData.detectedActions?.length > 0)) && (
          <div className="ml-11 -mt-1">
            <div className="flex flex-wrap gap-1 mb-2">
            {(mergedData.intentLog?.detectedActions || mergedData.detectedActions || []).map((action, idx) => {
                const executedAction = Array.isArray(mergedData.actionLog?.executed) 
                  ? mergedData.actionLog.executed.find(exec => exec.type === action.type)
                  : undefined;
                const statusColor = executedAction?.status === 'success' ? 'bg-green-100 text-green-700' :
                                   executedAction?.status === 'error' ? 'bg-red-100 text-red-700' :
                                   executedAction?.status === 'skipped' ? 'bg-gray-100 text-gray-600' :
                                   'bg-blue-100 text-blue-700';
                
                return (
                  <span key={idx} className={`inline-block rounded px-2 py-1 text-xs font-medium ${statusColor}`}>
                    {action.type}
                    {executedAction && (
                      <span className="ml-1">
                        {executedAction.status === 'success' ? '✓' : 
                         executedAction.status === 'error' ? '✗' : 
                         executedAction.status === 'skipped' ? '⊘' : '⏳'}
                      </span>
                    )}
                  </span>
                );
              })}
            </div>
            {Array.isArray(mergedData.actionLog?.executed) && mergedData.actionLog.executed.length > 0 && (
              <div className="text-xs text-gray-500">
                <div className="space-y-1">
                  {mergedData.actionLog.executed.map((execution, idx) => (
                    <div key={idx} className="flex justify-between items-center">
                      <span>{execution.type}</span>
                      <span className="flex items-center gap-1">
                        <span className={execution.status === 'success' ? 'text-green-600' : 
                                       execution.status === 'error' ? 'text-red-600' : 'text-gray-500'}>
                          {execution.status}
                        </span>
                        {execution.duration && <span>({execution.duration}ms)</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )
    },
    // SQL Edit Mode step
    {
      name: 'SQL Edit Mode',
      icon: FileSearch,
      iconColor: mergedData.sqlEditMode ? 'text-green-600' : 'text-gray-500',
      bgColor: 'bg-green-50',
      active: mergedData.sqlEditMode || false,
      status: (() => {
        if (mergedData.sqlEditModeEnabled === false) return 'inactive';
        if (mergedData.sqlEditMode === true) return mergedData.editResult?.success ? 'success' : 'failed';
        return 'skipped';
      })(),
      detail: (() => {
        if (mergedData.sqlEditModeEnabled === false) return 'Not enabled';
        
        // If main query used edit mode
        if (mergedData.sqlEditMode === true) {
          const editContext = mergedData.retryCount > 0 ? ' (retry)' : 
                            mergedData.isFollowUp ? ' (follow-up)' : '';
          return mergedData.editResult ? 
            `${mergedData.editResult.appliedCount || 0}/${mergedData.editResult.totalEdits || 0} edits applied${editContext}` :
            `No edit info${editContext}`;
        }
        
        // Explain why edit mode wasn't used
        if (!mergedData.isFollowUp && !mergedData.retryCount) {
          return 'Not applicable (new query)';
        }

        // If it was a follow-up or retry but SQL was too short
        const charCount = mergedData.originalSQLCharCount;
        if (charCount && charCount < (mergedData.sqlEditMinChars || 400)) {
          return `Not eligible (${charCount} chars < ${mergedData.sqlEditMinChars || 400})`;
        }
        
        // If eligible but LLM chose not to use it
        if (mergedData.sqlEditModeEligible && !mergedData.sqlEditModeUsed) {
          return 'Eligible but LLM chose complete SQL';
        }
        
        return 'Not applicable';
      })(),
      extraContent: mergedData.sqlEditMode && mergedData.editJson && (
        <div className="ml-11 -mt-1">
          <div className="space-y-1 text-xs">
            {mergedData.editJson.edits?.slice(0, 3).map((edit, idx) => (
              <div key={idx} className="bg-white border border-green-200 rounded p-2">
                <div className="font-medium text-green-700">Edit #{idx + 1}:</div>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <div>
                    <span className="text-gray-500">Find:</span>
                    <div className="font-mono text-red-600 text-xs truncate" title={edit.find}>
                      {edit.find}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">Replace:</span>
                    <div className="font-mono text-green-600 text-xs truncate" title={edit.replace}>
                      {edit.replace || '(delete)'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {mergedData.editJson.edits?.length > 3 && (
              <div className="text-gray-500 italic">
                ... and {mergedData.editJson.edits.length - 3} more edits
              </div>
            )}
            {mergedData.editResult?.failedEdits?.length > 0 && (
              <div className="text-red-600 font-medium mt-2">
                {mergedData.editResult.failedEdits.length} edit(s) failed
              </div>
            )}
          </div>
        </div>
      )
    },
    // Add Spray Review step
    {
      name: 'Spray Review',
      icon: Database,
      iconColor: mergedData.sprayEnabled ? 'text-blue-600' : 'text-gray-500',
      bgColor: 'bg-blue-50',
      active: mergedData.sprayEnabled,
      status: !mergedData.sprayEnabled ? 'inactive' : 
               !mergedData.sprayReviewLog?.success ? 'error' :
               mergedData.sprayReviewLog?.modified ? 'warning' : 'success',
      detail: !mergedData.sprayEnabled ? 'Not enabled' :
               !mergedData.sprayReviewLog?.success ? 'Failed' :
               mergedData.sprayReviewLog?.modified ? 'SQL Modified' : 'Selected Best SQL',
      extraContent: mergedData.sprayReviewLog?.success && mergedData.spraySelected && (
        <div className="ml-11 -mt-1 text-sm text-gray-500">
          Selected: <span className="font-medium">{mergedData.spraySelected}</span>
        </div>
      )
    },
    {
      name: 'Multi-Table Review',
      icon: ArrowRight,
      iconColor: 'text-amber-600',
      bgColor: 'bg-amber-50',
      active: mergedData.reviewLog?.enabled || 
              mergedData.queryVersions?.some(v => v.reviewLog?.enabled),
      status: (() => {
        // Check if review was performed in any version
        const reviewPerformed = mergedData.queryVersions?.find(v => v.reviewLog?.enabled && !v.reviewLog?.skipped);
        if (reviewPerformed) {
          return !reviewPerformed.reviewLog.success ? 'error' :
                 reviewPerformed.reviewLog.valid ? 'success' : 'warning';
        }
        // Otherwise use the final reviewLog
        return !mergedData.reviewLog?.enabled ? 'inactive' : 
                mergedData.reviewLog?.skipped ? 'skipped' :
                !mergedData.reviewLog?.success ? 'error' :
                mergedData.reviewLog?.valid ? 'success' : 'warning';
      })(),
      detail: (() => {
        // Check if review was performed in any version
        const reviewFromVersion = mergedData.queryVersions?.find(v => v.reviewLog?.enabled && !v.reviewLog?.skipped);
        const reviewLog = reviewFromVersion?.reviewLog || mergedData.reviewLog;
        
        if (reviewLog && reviewLog.enabled && !reviewLog.skipped) {
          let detail = !reviewLog.success ? 'Failed' :
                       reviewLog.valid ? 'Valid SQL' : 'SQL Modified';
          
          // Add edit mode information ONLY if the review itself used edit mode
          if (reviewLog.sqlEditMode && reviewLog.sqlEditModeUsed && reviewLog.editResult) {
            detail += ` (${reviewLog.editResult.appliedCount}/${reviewLog.editResult.totalEdits} edits)`;
          }
          
          // If it was later skipped, add that info
          if (mergedData.reviewLog?.skipped && reviewFromVersion) {
            return `${detail} (Retry: Skipped)`;
          }
          return detail;
        }
        
        // Otherwise use the final reviewLog
        return !mergedData.reviewLog?.enabled ? 'Not enabled' :
                mergedData.reviewLog?.skipped ? 'Skipped' :
                !mergedData.reviewLog?.success ? 'Failed' :
                mergedData.reviewLog?.valid ? 'Valid SQL' : 'SQL Modified';
      })(),
      extraContent: (
        <div className="ml-11 -mt-1 text-sm text-gray-500">
          {/* Show skip reason if final attempt was skipped */}
          {mergedData.reviewLog?.skipReason && (
            <div className="italic">Reason: {mergedData.reviewLog.skipReason}</div>
          )}
          {/* Show edit information if review used edit mode */}
          {(mergedData.reviewLog?.sqlEditMode && mergedData.reviewLog?.editJson) && (
            <div className="mt-2 space-y-1 text-xs">
              <div className="font-medium text-amber-700">Review used SQL Edit Mode:</div>
              {mergedData.reviewLog.editJson.edits?.slice(0, 2).map((edit, idx) => (
                <div key={idx} className="bg-white border border-amber-200 rounded p-2">
                  <div className="font-medium text-amber-600">Edit #{idx + 1}:</div>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <div>
                      <span className="text-gray-500">Find:</span>
                      <div className="font-mono text-red-600 text-xs truncate" title={edit.find}>
                        {edit.find}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500">Replace:</span>
                      <div className="font-mono text-green-600 text-xs truncate" title={edit.replace}>
                        {edit.replace || '(delete)'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {mergedData.reviewLog.editJson.edits?.length > 2 && (
                <div className="text-gray-500 italic">
                  ... and {mergedData.reviewLog.editJson.edits.length - 2} more edits
                </div>
              )}
            </div>
          )}
          {/* Show review history if there were multiple attempts */}
          {mergedData.queryVersions?.filter(v => v.reviewLog).length > 1 && (
            <div className="mt-1 text-xs">
              Review history: {mergedData.queryVersions.map((v, idx) => 
                v.reviewLog ? `v${idx + 1}: ${v.reviewLog.skipped ? 'Skipped' : v.reviewLog.valid ? 'Valid' : 'Modified'}` : null
              ).filter(Boolean).join(', ')}
            </div>
          )}
        </div>
      )
    },
    {
      name: 'Investigation',
      icon: FileSearch,
      iconColor: mergedData.wasInvestigated ? 'text-amber-600' : 'text-gray-500',
      bgColor: 'bg-gray-100',
      active: mergedData.wasInvestigated,
      detail: mergedData.wasInvestigated ? 'Applied' : 'Not applied'
    },
    {
      name: 'Beyond Analytics',
      icon: Zap,
      iconColor: mergedData.beyondAgentCalled ? 'text-purple-600' : 'text-gray-500',
      bgColor: 'bg-purple-50',
      active: mergedData.beyondAgentCalled && mergedData.beyondAnalytics?.analyticsRequired,
      detail: !mergedData.beyondAgentCalled ? 'Not called' :
              !mergedData.beyondAnalytics?.analyticsRequired ? 'No analytics needed' :
              (() => {
                const functionCount = (mergedData.beyondAnalytics?.functions || []).length;
                const hasFallback = mergedData.beyondAnalytics?.functions?.some(f => 
                  f.fallbackUsed || (f.actualMethod && f.actualMethod !== f.requestedMethod)
                );
                return `${functionCount} function(s) applied${hasFallback ? ' (with fallback)' : ''}`;
              })()
    },
    {
      name: 'Retries',
      icon: RotateCw,
      iconColor: (mergedData.retryCount || 0) > 0 ? 'text-blue-600' : 'text-gray-500',
      bgColor: 'bg-gray-100',
      active: (mergedData.retryCount || 0) > 0,
      detail: (mergedData.retryCount || 0) > 0 
        ? `${mergedData.retryCount} (${mergedData.retryResult || 'Unknown result'})` 
        : 'None'
    }
  ];

  // Render versions section
  const renderVersions = () => {
    // Get versions from result
    let versions = mergedData.queryVersions;
    if (!versions && mergedData.data) {
      versions = mergedData.data.queryVersions;
    }
    
    // Ensure we have an array
    versions = Array.isArray(versions) ? versions : [];
    
    if (versions.length === 0) {
      return <div className="text-gray-500 italic">No version history available</div>;
    }

    return (
      <div className="space-y-4">
        {versions.map((version, index) => {
          // Extract data from version
          const sqlQuery = version.sqlQuery || 
                          version.context?.modifiedSqlQuery ||
                          version.completePrompt?.context?.modifiedSqlQuery;
                          
          const bigQueryError = version.bigQueryError ||
                               version.context?.bigQueryError ||
                               version.completePrompt?.context?.bigQueryError;
                               
          const versionModel = version.completePrompt?.model;
          const versionTemp = version.completePrompt?.temperature;
          
          return (
            <div key={index} className="border rounded-lg p-4 bg-gray-50">
              <div className="font-semibold text-gray-700 mb-2">
                Version {version.version || (index + 1)}
                {version.isInvestigation && " (Investigation)"}
              </div>
              
              {sqlQuery && (
                <div className="mb-2">
                  <div className="text-sm font-medium text-gray-600 mb-1 flex justify-between items-center">
                    <span>SQL Query:</span>
                    <button 
                      className="p-1 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded"
                      onClick={() => copyToClipboard(sqlQuery)}
                      title="Copy SQL"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <SyntaxHighlighter 
                    language="sql" 
                    style={docco} 
                    customStyle={{ fontSize: '0.875rem', backgroundColor: 'white' }}
                  >
                    {sqlQuery}
                  </SyntaxHighlighter>
                  
                  {versions[index + 1]?.bigQueryError && (
                    <div className="mt-2 mb-4 border-l-4 border-red-500 pl-3">
                      <div className="text-sm font-medium text-red-600 mb-1">Error:</div>
                      <div className="text-sm bg-red-50 text-red-700 p-2 rounded">
                        {versions[index + 1]?.bigQueryError}
                      </div>
                    </div>
                  )}
                </div>
              )}
          
              {version.modifiedSqlQuery && version.modifiedSqlQuery !== sqlQuery && (
                <div className="mb-2">
                  <div className="text-sm font-medium text-gray-600 mb-1 flex justify-between items-center">
                    <span>Modified SQL Query:</span>
                    <button 
                      className="p-1 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded"
                      onClick={() => copyToClipboard(version.modifiedSqlQuery)}
                      title="Copy SQL"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <SyntaxHighlighter 
                    language="sql" 
                    style={docco} 
                    customStyle={{ fontSize: '0.875rem', backgroundColor: 'white' }}
                  >
                    {version.modifiedSqlQuery}
                  </SyntaxHighlighter>
                </div>
              )}
          
             {(versionModel || versionTemp || version.sqlEditMode) && (
                <div className="mt-2 text-xs text-gray-500">
                  {versionModel && <div>Model: {versionModel}</div>}
                  {versionTemp && <div>Temperature: {versionTemp}</div>}
                  {version.sqlEditMode && (
                    <div className="text-green-600">
                      SQL Edit Mode: {version.editResult?.appliedCount || 0}/{version.editResult?.totalEdits || 0} edits
                      {version.isRetry && ' (retry)'}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Helper to render config in a more readable format
  const renderConfig = (config) => {
    if (!config) return <div className="text-gray-500 italic">No configuration available</div>;
    
    return (
      <>
        {Object.entries(config).map(([key, value]) => (
          <div key={key} className="mb-2 last:mb-0">
            <div className="font-medium text-gray-600">{key}:</div>
            <div className="ml-4">
              {renderConfigValue(value)}
            </div>
          </div>
        ))}
      </>
    );
  };
  
  // Helper to render different value types
  const renderConfigValue = (value) => {
    if (value === null || value === undefined) return <span className="text-gray-500">None</span>;
    
    if (typeof value === 'boolean') {
      return <span className="text-gray-800">{value ? 'Yes' : 'No'}</span>;
    }
    
    if (Array.isArray(value)) {
      return (
        <div className="text-gray-800">
          {value.length === 0 ? <span className="text-gray-500">Empty array</span> : (
            value.map((item, idx) => (
              <div key={idx} className="mb-1 last:mb-0">
                • {typeof item === 'object' ? JSON.stringify(item) : item.toString()}
              </div>
            ))
          )}
        </div>
      );
    }
    
    if (typeof value === 'object') {
      return (
        <div className="pl-2 border-l-2 border-gray-200">
          {Object.entries(value).map(([k, v]) => (
            <div key={k} className="mb-1 last:mb-0">
              <span className="text-gray-600">{k}:</span>{' '}
              <span className="text-gray-800">
                {typeof v === 'object' ? JSON.stringify(v) : v?.toString()}
              </span>
            </div>
          ))}
        </div>
      );
    }
    
    return <span className="text-gray-800">{value.toString()}</span>;
  };

  // Render Beyond Analytics section if available
  const renderBeyondAnalyticsSection = () => {
    if (!mergedData.beyondAgentCalled || !mergedData.beyondAnalytics) {
      return null;
    }
    
    return (
      <div className="mb-4">
        <div className="font-medium text-gray-700 mb-2">
          <div className="flex items-center">
            <Zap className="w-5 h-5 mr-1.5 text-purple-600" />
            Beyond Analytics:
          </div>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
          <div className="mb-2">
            <span className="text-sm text-gray-600">Analytics Required:</span>{' '}
            <span className={`font-medium ${mergedData.beyondAnalytics.analyticsRequired ? 'text-green-600' : 'text-gray-600'}`}>
              {mergedData.beyondAnalytics.analyticsRequired ? 'Yes' : 'No'}
            </span>
          </div>
          
          {mergedData.beyondAnalytics.functions && mergedData.beyondAnalytics.functions.length > 0 && (
            <div>
              <div className="text-sm font-medium text-gray-600 mb-2">Applied Functions:</div>
              <div className="space-y-2">
              {mergedData.beyondAnalytics.functions.map((func, idx) => (
                <div key={idx} className="bg-white p-3 rounded border border-gray-200">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-medium text-gray-700">{func.name}</div>
                      {/* Show fallback information if method was changed */}
                      {(func.fallbackUsed || (func.actualMethod && func.actualMethod !== func.requestedMethod)) && (
                        <div className="text-xs text-amber-600 mt-1">
                          Fallback: {func.requestedMethod || func.config?.method} → {func.actualMethod || 'alternative method'}
                        </div>
                      )}
                    </div>
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">
                      {func.visualization}
                    </span>
                  </div>
                  
                  {func.config && (
                    <div className="text-sm text-gray-600 space-y-1">
                      {Object.entries(func.config).map(([key, value]) => (
                        <div key={key} className="flex">
                          <span className="text-gray-500 min-w-[140px]">{key}:</span>
                          <span className="font-medium">{typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              </div>
            </div>
          )}
          
          {mergedData.beyondAgentTimestamp && (
            <div className="mt-2 text-xs text-gray-500">
              First processed at: {formatTime(mergedData.beyondAgentTimestamp)}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render Spray information section if available - with improved error handling
  const renderSpraySection = () => {
    // First check if Spray was even enabled
    if (!mergedData.sprayEnabled) {
      return null;
    }
    
    // Handle different sprayCandidates formats (array, object, or non-existent)
    let candidatesArray = [];
    
    if (mergedData.sprayCandidates) {
      // If it's already an array, use it directly
      if (Array.isArray(mergedData.sprayCandidates)) {
        candidatesArray = mergedData.sprayCandidates;
      } 
      // If it's an object with keys (Firestore often stores arrays as objects with numeric keys)
      else if (typeof mergedData.sprayCandidates === 'object') {
        // Convert object to array
        candidatesArray = Object.keys(mergedData.sprayCandidates)
          .map(key => mergedData.sprayCandidates[key])
          // Filter out any non-object values
          .filter(item => typeof item === 'object' && item !== null);
      }
    }
    
    // If we have no candidates to display after processing, return null
    if (candidatesArray.length === 0) {
      return null;
    }

    return (
      <div className="mb-4">
        <div className="font-medium text-gray-700 mb-2">
          <div className="flex items-center">
            <Database className="w-5 h-5 mr-1.5 text-blue-600" />
            Spray Review Candidates:
          </div>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 space-y-3">
          {candidatesArray.map((candidate, index) => {
            // Ensure this candidate has the expected properties
            if (!candidate || !candidate.sqlQuery) {
              // Skip this candidate if it doesn't have sqlQuery
              return null;
            }
            
            return (
              <div key={index} className={`p-3 rounded border ${mergedData.spraySelected === index ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                <div className="text-sm font-medium text-gray-700 mb-1 flex justify-between items-center">
                  <span>
                    Candidate #{index} 
                    {mergedData.spraySelected === index && <span className="ml-2 text-green-600">(Selected)</span>}
                    {candidate.modelName && <span className="ml-2 text-gray-500">- Model: {candidate.modelName}</span>}
                  </span>
                  <button 
                    className="p-1 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded"
                    onClick={() => copyToClipboard(candidate.sqlQuery)}
                    title="Copy SQL"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <SyntaxHighlighter 
                  language="sql" 
                  style={docco} 
                  customStyle={{ 
                    fontSize: '0.875rem', 
                    backgroundColor: mergedData.spraySelected === index ? 'rgba(240, 253, 244, 1)' : 'white',
                    borderRadius: '4px'
                  }}
                >
                  {candidate.sqlQuery}
                </SyntaxHighlighter>
              </div>
            );
          })}

          {mergedData.sprayReviewLog?.modified && (
            <div className="p-3 rounded border border-blue-500 bg-blue-50">
              <div className="text-sm font-medium text-blue-700 mb-1 flex justify-between items-center">
                <span>Modified SQL (by Spray Review)</span>
                <button 
                  className="p-1 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded"
                  onClick={() => copyToClipboard(
                    mergedData.spraySelected === "review_agent_modified" && mergedData.queryVersions && mergedData.queryVersions.length > 0
                      ? mergedData.queryVersions[0].sqlQuery
                      : (mergedData.sprayModifiedSql || mergedData.sqlQuery)
                  )}
                  title="Copy SQL"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <SyntaxHighlighter 
                language="sql" 
                style={docco} 
                customStyle={{ 
                  fontSize: '0.875rem', 
                  backgroundColor: 'rgba(239, 246, 255, 1)',
                  borderRadius: '4px'
                }}
              >
                {/* If spray review modified the SQL, use the first version's SQL, otherwise fall back to previous logic */}
                {mergedData.spraySelected === "review_agent_modified" && mergedData.queryVersions && mergedData.queryVersions.length > 0
                  ? mergedData.queryVersions[0].sqlQuery
                  : (mergedData.sprayModifiedSql || mergedData.sqlQuery)}
              </SyntaxHighlighter>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render Action Execution information section if available
  const renderActionExecutionSection = () => {
    if (!mergedData.actionLog?.enabled || (!mergedData.detectedActions?.length && (!Array.isArray(mergedData.actionLog?.executed) || mergedData.actionLog.executed.length === 0))) {
      return null;
    }
    
    return (
      <div className="mb-4">
        <div className="font-medium text-gray-700 mb-2">
          <div className="flex items-center">
            <Zap className="w-5 h-5 mr-1.5 text-purple-600" />
            Action Execution Details:
          </div>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
          
          {/* Detected Actions */}
          {mergedData.detectedActions?.length > 0 && (
            <div className="mb-3">
              <div className="text-sm font-medium text-gray-600 mb-2">Detected Actions:</div>
              <div className="space-y-2">
                {mergedData.detectedActions.map((action, idx) => (
                  <div key={idx} className="bg-white p-2 rounded border border-gray-200">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="font-medium text-gray-700">{action.type}</span>
                        {action.params && (
                          <div className="text-xs text-gray-500 mt-1">
                            {Object.entries(action.params).map(([key, value]) => (
                              <div key={key}>
                                <span className="font-medium">{key}:</span> {JSON.stringify(value)}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Execution Results */}
          {Array.isArray(mergedData.actionLog?.executed) && mergedData.actionLog.executed.length > 0 && (
            <div>
              <div className="text-sm font-medium text-gray-600 mb-2">Execution Results:</div>
              <div className="space-y-2">
                {mergedData.actionLog.executed.map((execution, idx) => (
                  <div key={idx} className="bg-white p-2 rounded border border-gray-200">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-700">{execution.type}</span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            execution.status === 'success' ? 'bg-green-100 text-green-700' :
                            execution.status === 'error' ? 'bg-red-100 text-red-700' :
                            execution.status === 'skipped' ? 'bg-gray-100 text-gray-600' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {execution.status}
                          </span>
                        </div>
                        
                        {execution.error && (
                          <div className="text-xs text-red-600 mt-1">
                            Error: {execution.error}
                          </div>
                        )}
                        
                        {execution.result && (
                          <div className="text-xs text-gray-600 mt-1">
                            {execution.result.message && <div>Result: {execution.result.message}</div>}
                            {execution.result.appliedSettings && (
                              <div>Applied: {JSON.stringify(execution.result.appliedSettings)}</div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="text-xs text-gray-500 text-right">
                        {execution.duration && <div>{execution.duration}ms</div>}
                        {execution.timestamp && (
                          <div>{formatTime(execution.timestamp)}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <PopupCard
        isOpen={isOpen}
        onClose={onClose}
        title="Debug Logs"
        className="z-50 font-sans"
        contentClassName="w-full max-w-3xl"
      >
        <div className="p-4 text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>Loading document data...</p>
        </div>
      </PopupCard>
    );
  }

  if (error) {
    return (
      <PopupCard
        isOpen={isOpen}
        onClose={onClose}
        title="Debug Logs"
        className="z-50 font-sans"
        contentClassName="w-full max-w-3xl"
      >
        <div className="p-4 text-center">
          <div className="text-red-500 mb-4">
            <AlertCircle className="h-8 w-8 mx-auto" />
          </div>
          <p className="text-red-600">{error}</p>
          <button
            onClick={onClose}
            className="mt-4 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </PopupCard>
    );
  }

  return (
    <PopupCard
      isOpen={isOpen}
      onClose={onClose}
      title="Debug Logs"
      className="z-50 font-sans"
      contentClassName="w-full max-w-3xl"
    >
      <div className="p-4">
        {/* Document ID and Version Count - Improved UI */}
        <div className="mb-4 rounded-lg overflow-hidden border border-gray-200">
          <div className="bg-gray-50 py-2 px-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="font-semibold text-gray-700">Query Information</h3>
            <div className="text-gray-600 text-sm">
              Versions: <span className="font-medium">{(mergedData.queryVersions || []).length}</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
            {/* Document ID */}
            <div className="flex flex-col">
              <div className="font-medium text-gray-700 mb-1.5">Document ID:</div>
              <div className="flex items-center">
                <code className="bg-gray-100 px-3 py-1.5 rounded border text-sm select-all w-full font-mono">{mergedData.documentId}</code>
              </div>
            </div>
            
            {/* Tango ID */}
            <div className="flex flex-col">
              <div className="font-medium text-gray-700 mb-1.5">Tango ID:</div>
              <div 
                className="bg-primary/10 text-primary px-3 py-1.5 rounded-lg border border-primary/20 inline-flex items-center cursor-pointer hover:bg-primary/20 transition-colors duration-200 select-all"
                onClick={handleTangoIdCopy}
              >
                <span id={`tango-id-${mergedData.documentId}`} className="font-medium">{`tango_${mergedData.documentId}`}</span>
                <ExternalLink className="ml-1.5 w-3.5 h-3.5" />
              </div>
            </div>
          </div>
        </div>

        {/* Processing Details */}
        <div className="mb-4">
          <div className="font-medium text-gray-700 mb-2">Processing Details:</div>
          <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
            {/* Processing Time */}
            {(mergedData.startTime && mergedData.endTime) && (
              <div className="border-b border-gray-200 pb-2 mb-2">
                <div className="flex items-center text-gray-700 bg-blue-50 p-2 rounded-md">
                  <div className="font-medium mr-1">Total Processing Time:</div>
                  <div className="text-gray-800 font-mono">
                    {(() => {
                      try {
                        // For Firestore timestamps
                        if (mergedData.startTime.seconds !== undefined && mergedData.endTime.seconds !== undefined) {
                          const startSeconds = mergedData.startTime.seconds + (mergedData.startTime.nanoseconds || 0) / 1e9;
                          const endSeconds = mergedData.endTime.seconds + (mergedData.endTime.nanoseconds || 0) / 1e9;
                          const diffSeconds = endSeconds - startSeconds;
                          return `${diffSeconds.toFixed(2)} seconds`;
                        }
                        
                        // For other date formats
                        const startDate = new Date(mergedData.startTime);
                        const endDate = new Date(mergedData.endTime);
                        const diffMs = endDate - startDate;
                        return `${(diffMs / 1000).toFixed(2)} seconds`;
                        
                      } catch (e) {
                        console.error('Error calculating time difference:', e);
                        return 'Unable to calculate';
                      }
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* Processing Pipeline */}
            <div className="flex flex-col space-y-2">
              {pipelineSteps.map((step, index) => (
                <React.Fragment key={index}>
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-full ${step.bgColor} flex items-center justify-center mr-3`}>
                      <step.icon className={`w-4 h-4 ${step.iconColor}`} />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium flex items-center">
                        {step.name}
                        <span 
                          className={`inline-block w-2 h-2 rounded-full ml-2 ${
                            step.status === 'success' ? 'bg-green-500' :
                            step.status === 'warning' ? 'bg-amber-500' :
                            step.status === 'error' ? 'bg-red-500' :
                            step.status === 'skipped' ? 'bg-gray-400' :
                            step.active ? 'bg-green-500' : 'bg-gray-400'
                          }`}
                        />
                      </div>
                      <div className="text-sm text-gray-600">{step.detail}</div>
                    </div>
                  </div>
                  {step.extraContent}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* User Query */}
        <div className="mb-4">
          <div className="font-medium text-gray-700 mb-2">User Query:</div>
          <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
            {mergedData.userQuery || mergedData.userMessage || 'No query available'}
          </div>
        </div>
        
        {/* Action Execution Section */}
        {renderActionExecutionSection()}
        
        {/* Beyond Analytics Section */}
        {renderBeyondAnalyticsSection()}
        
        {/* Spray Section */}
        {renderSpraySection()}
        
        {/* SQL Review and Modifications */}
        {/* Original SQL vs SQL After Review - Check both top level and queryVersions */}
        {(() => {
        // First check top-level fields (for backward compatibility)
        if (mergedData.originalSqlBeforeReview && (mergedData.reviewedSql || mergedData.reviewLog?.normalizedSQL)) {
          // Use normalizedSQL if review used SQL edit mode, otherwise use reviewedSql
          const reviewedSqlToDisplay = mergedData.reviewLog?.editJson ? 
            mergedData.reviewLog.normalizedSQL : mergedData.reviewedSql;
          
          return (
            <div className="mb-4">
              <div className="font-medium text-gray-700 mb-2">
                <div className="flex items-center">
                  <ArrowRight className="w-5 h-5 mr-1.5 text-amber-600" />
                  Review Stage SQL Modification:
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 grid grid-cols-1 gap-3">
                <div>
                  <div className="text-sm font-medium text-gray-600 mb-1 flex justify-between items-center">
                    <span>Original SQL:</span>
                    <button 
                      className="p-1 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded"
                      onClick={() => copyToClipboard(mergedData.originalSqlBeforeReview)}
                      title="Copy SQL"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <SyntaxHighlighter 
                    language="sql" 
                    style={docco} 
                    customStyle={{ fontSize: '0.875rem', backgroundColor: 'white' }}
                  >
                    {mergedData.originalSqlBeforeReview}
                  </SyntaxHighlighter>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-600 mb-1 flex justify-between items-center">
                    <span>Reviewed SQL:</span>
                    <button 
                      className="p-1 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded"
                      onClick={() => copyToClipboard(reviewedSqlToDisplay)}
                      title="Copy SQL"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <SyntaxHighlighter 
                    language="sql" 
                    style={docco} 
                    customStyle={{ fontSize: '0.875rem', backgroundColor: 'white' }}
                  >
                    {reviewedSqlToDisplay}
                  </SyntaxHighlighter>
                </div>
              </div>
            </div>
          );
        }
        
        // Otherwise check queryVersions for review modifications
        const versionWithReview = mergedData.queryVersions?.find(v => 
          v.originalSqlBeforeReview && (v.modifiedSqlQuery || v.reviewLog?.normalizedSQL) && v.reviewLog?.enabled && !v.reviewLog?.skipped
        );
        
        if (versionWithReview) {
          // Use normalizedSQL if review used SQL edit mode, otherwise use modifiedSqlQuery
          const reviewedSqlToDisplay = versionWithReview.reviewLog?.editJson ? 
            versionWithReview.reviewLog.normalizedSQL : versionWithReview.modifiedSqlQuery;
          
          return (
            <div className="mb-4">
              <div className="font-medium text-gray-700 mb-2">
                <div className="flex items-center">
                  <ArrowRight className="w-5 h-5 mr-1.5 text-amber-600" />
                  Review Stage SQL Modification:
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 grid grid-cols-1 gap-3">
                <div>
                  <div className="text-sm font-medium text-gray-600 mb-1 flex justify-between items-center">
                    <span>Original SQL:</span>
                    <button 
                      className="p-1 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded"
                      onClick={() => copyToClipboard(versionWithReview.originalSqlBeforeReview)}
                      title="Copy SQL"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <SyntaxHighlighter 
                    language="sql" 
                    style={docco} 
                    customStyle={{ fontSize: '0.875rem', backgroundColor: 'white' }}
                  >
                    {versionWithReview.originalSqlBeforeReview}
                  </SyntaxHighlighter>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-600 mb-1 flex justify-between items-center">
                    <span>Reviewed SQL:</span>
                    <button 
                      className="p-1 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded"
                      onClick={() => copyToClipboard(reviewedSqlToDisplay)}
                      title="Copy SQL"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <SyntaxHighlighter 
                    language="sql" 
                    style={docco} 
                    customStyle={{ fontSize: '0.875rem', backgroundColor: 'white' }}
                  >
                    {reviewedSqlToDisplay}
                  </SyntaxHighlighter>
                </div>
              </div>
            </div>
          );
        }
        
        return null;
      })()}

        {/* Review History - if multiple review attempts */}
        {mergedData.queryVersions?.filter(v => v.reviewLog).length > 0 && (
          <div className="mb-4">
            <div className="font-medium text-gray-700 mb-2">
              <div className="flex items-center">
                <ArrowRight className="w-5 h-5 mr-1.5 text-amber-600" />
                Multi-Table Review History:
              </div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 space-y-2">
              {mergedData.queryVersions.map((version, idx) => 
                version.reviewLog ? (
                  <div key={idx} className="p-2 bg-white rounded border border-gray-200">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="font-medium text-gray-700">
                          Attempt {version.version || idx + 1}:
                        </span>
                        <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                          version.reviewLog.skipped ? 'bg-gray-100 text-gray-600' :
                          !version.reviewLog.success ? 'bg-red-100 text-red-700' :
                          version.reviewLog.valid ? 'bg-green-100 text-green-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {version.reviewLog.skipped ? 'Skipped' :
                          !version.reviewLog.success ? 'Failed' :
                          version.reviewLog.valid ? 'Valid' : 'Modified'}
                        </span>
                      </div>
                      {version.reviewLog.processingTime && (
                        <span className="text-xs text-gray-500">
                          {(version.reviewLog.processingTime / 1000).toFixed(2)}s
                        </span>
                      )}
                    </div>
                    {version.reviewLog.skipReason && (
                      <div className="text-xs text-gray-500 mt-1">
                        Reason: {version.reviewLog.skipReason}
                      </div>
                    )}
                    {version.reviewLog.model && (
                      <div className="text-xs text-gray-500 mt-1">
                        Model: {version.reviewLog.model}
                      </div>
                    )}
                  </div>
                ) : null
              )}
            </div>
          </div>
        )}

        {/* Configuration */}
          <div className="mb-4">
            <div className="font-medium text-gray-700 mb-2">Configuration:</div>
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
              <div className="space-y-2">
                <div>
                  <span className="font-medium text-gray-600">Model:</span>{' '}
                  <span className="text-gray-800">{mergedData.llmModel || mergedData.usedModel || 'Unknown'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Original Model:</span>{' '}
                  <span className="text-gray-800">{mergedData.originalModel || 'Unknown'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Fallback Used:</span>{' '}
                  <span className="text-gray-800">{mergedData.fallbackUsed ? 'Yes' : 'No'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Intent Detection:</span>{' '}
                  <span className="text-gray-800">{mergedData.intentOn ? 'Enabled' : 'Disabled'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Matching:</span>{' '}
                  <span className="text-gray-800">{mergedData.matchingOn ? 'Enabled' : 'Disabled'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Spray:</span>{' '}
                  <span className="text-gray-800">{mergedData.sprayEnabled ? 'Enabled' : 'Disabled'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Sanitization:</span>{' '}
                  <span className="text-gray-800">{mergedData.sanitizationApplied ? 'Applied' : 'Not Applied'}</span>
                </div>
              </div>
            </div>
          </div>

        {/* Versions */}
        <div className="mb-4">
          <div className="font-medium text-gray-700 mb-2">Query Versions:</div>
          {renderVersions()}
        </div>
      </div>
    </PopupCard>
  );
};

export default LogsModal;